'use client';

import React, { useEffect } from 'react';
import { getUrlParameter } from '@/lib/utils/url-params';
import { CheckmarkIcon } from '@/components/Icons/Icons';

interface CategoryAccordionProps {
  title: string;
  data: string[];
  uidList: string[];
  parentUid: string;
  action: (checkedFilters: Array<{ uid: string; parentUid: string }>) => void;
  /** When filter param in URL changes (e.g. from contentType redirect), re-sync checkbox state */
  filterParamFromUrl?: string | null;
  /** When set, show a single "External sources" checkbox that lists these names in the description */
  externalSourceNames?: string[];
}

export function CategoryAccordion({
  title,
  data,
  uidList,
  parentUid,
  action,
  filterParamFromUrl,
  externalSourceNames,
}: CategoryAccordionProps) {
  const handleCheckbox = () => {
    // Defer to next tick so DOM checkbox state is updated before we read (fixes unchecked -> data not refreshing)
    const runAction = () => {
      const filterInputs = document.querySelectorAll(`input.explore-filter`);
      const checkedFilters: Array<{ uid: string; parentUid: string }> = [];

      filterInputs.forEach((box) => {
        const checkbox = box as HTMLInputElement;
        if (checkbox.checked) {
          const uid = checkbox.getAttribute('data-uid') || '';
          const parentUidValue = checkbox.getAttribute('data-puid') || '';
          checkedFilters.push({ uid, parentUid: parentUidValue });
        }
      });

      action(checkedFilters);
    };
    queueMicrotask(runAction);
  };

  useEffect(() => {
    const filterParam = filterParamFromUrl ?? getUrlParameter('filter');
    // Always uncheck all first so URL is the single source of truth (e.g. Catalog nav link reset)
    const filterInputs = document.querySelectorAll('input.explore-filter');
    filterInputs.forEach((box) => {
      (box as HTMLInputElement).checked = false;
    });
    if (filterParam) {
      try {
        const filters = JSON.parse(filterParam);
        if (Array.isArray(filters) && filters.length > 0) {
          filters.forEach((filter: { uid: string }) => {
            const filterElement = document.querySelector(
              `input[data-uid="${filter.uid}"]`
            ) as HTMLInputElement;
            if (filterElement) {
              filterElement.checked = true;
            }
          });
        }
      } catch (error) {
        // Error parsing filter parameter
      }
    }
  }, [filterParamFromUrl]);

  const titleId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="w-full">
      <p className="cs-caption mx-4 my-2">{title}</p>
      <div className="">
      <div className="pt-4 pb-8">
        {data.map((el, i) => {
          const checkboxId = `flexCheckDefault-${titleId}-${i}`;
          return (
            <div className={`flex items-center gap-3 ${i < data.length - 1 ? 'mb-2' : ''}`} key={i}>
              <div className="relative inline-flex items-center justify-center checkbox-wrapper h-4 w-4 shrink-0">
                <input
                  className="explore-filter h-4 w-4 border border-border-medium [&:not(:checked)]:hover:border-text-default-medium bg-transparent text-icon-subtle focus:ring-amethyst-accent cursor-pointer appearance-none checked:border-amethyst-accent checked:bg-amethyst-accent"
                  type="checkbox"
                  value={el}
                  onChange={handleCheckbox}
                  id={checkboxId}
                  data-uid={uidList[i]}
                  data-puid={parentUid}
                />
                <CheckmarkIcon
                  className="checkbox-checkmark absolute top-0 left-0 h-4 w-4 pointer-events-none opacity-0"
                />
              </div>
              <label
                className="cs-s-body-regular text-text-default-strong cursor-pointer flex-1 leading-4"
                htmlFor={checkboxId}
              >
                {el}
              </label>
            </div>
          );
        })}
        {parentUid === 'content_type' && (
          <div className="flex items-start gap-3 mt-2 pt-4">
            <div className="relative inline-flex items-center justify-center checkbox-wrapper h-4 w-4 shrink-0 mt-0.5">
              <input
                className="explore-filter h-4 w-4 border border-border-medium [&:not(:checked)]:hover:border-text-default-medium bg-transparent text-icon-subtle focus:ring-amethyst-accent cursor-pointer appearance-none checked:border-amethyst-accent checked:bg-amethyst-accent"
                type="checkbox"
                onChange={handleCheckbox}
                id={`flexCheckDefault-${titleId}-external-sources`}
                data-uid="external-sources"
                data-puid={parentUid}
              />
              <CheckmarkIcon
                className="checkbox-checkmark absolute top-0 left-0 h-4 w-4 pointer-events-none opacity-0"
              />
            </div>
            <label
              className="cs-s-body-regular text-text-default-strong cursor-pointer flex-1 leading-4"
              htmlFor={`flexCheckDefault-${titleId}-external-sources`}
            >
              <span className="block">External sources</span>
              <span className="cs-caption block mt-1">
                {externalSourceNames != null && externalSourceNames.length > 0
                  ? `Include content from ${externalSourceNames.join(', ')}`
                  : ''}
              </span>
            </label>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

